import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Input } from "../../components/ui/input";
import { toast } from "../../hooks/use-toast";
import { Settings, Mail, X } from "lucide-react";
import { useAppStore } from "../../store/appStore";
import { InvoiceSettings, SettingChangeHistory } from "../../types/schema";
import { Badge } from "../../components/ui/badge";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleId: string;
}

export default function InvoiceSettingsModal({ open, onOpenChange, scheduleId }: Props) {
  const [sendEmailReminder, setSendEmailReminder] = useState(false);
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [currentToEmail, setCurrentToEmail] = useState("");
  const [currentCcEmail, setCurrentCcEmail] = useState("");

  const updateScheduleSettings = useAppStore(s => s.updateScheduleSettings);
  const schedules = useAppStore(s => s.schedules);
  const schedule = schedules.find(s => s.id === scheduleId);
  const existingSettings = schedule?.invoiceSettings;
  
  const handleAddToEmail = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentToEmail.trim()) {
      e.preventDefault();
      const email = currentToEmail.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !toEmails.includes(email)) {
        setToEmails([...toEmails, email]);
        setCurrentToEmail("");
      } else {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address.",
          variant: "destructive",
        });
      }
    }
  };
  
  const handleAddCcEmail = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentCcEmail.trim()) {
      e.preventDefault();
      const email = currentCcEmail.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !ccEmails.includes(email)) {
        setCcEmails([...ccEmails, email]);
        setCurrentCcEmail("");
      } else {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address.",
          variant: "destructive",
        });
      }
    }
  };
  
  const removeToEmail = (email: string) => {
    setToEmails(toEmails.filter(e => e !== email));
  };
  
  const removeCcEmail = (email: string) => {
    setCcEmails(ccEmails.filter(e => e !== email));
  };

  const handleSave = () => {
    const changeDescription = sendEmailReminder 
      ? `Email reminders enabled - To: ${toEmails.join(', ')} ${ccEmails.length > 0 ? `CC: ${ccEmails.join(', ')}` : ''}`
      : 'Email reminders disabled';
    
    const newChange: SettingChangeHistory = {
      id: `CHANGE-${Date.now()}`,
      date: new Date().toISOString(),
      user: "Current User", // TODO: Replace with actual user from auth
      description: changeDescription,
    };

    const settings: InvoiceSettings = {
      scheduleId,
      sendEmailReminder,
      reminderToEmails: sendEmailReminder ? toEmails : undefined,
      reminderCcEmails: sendEmailReminder ? ccEmails : undefined,
      updatedAt: new Date().toISOString(),
      updatedBy: "Current User", // TODO: Replace with actual user from auth
      changeHistory: [...(existingSettings?.changeHistory || []), newChange],
    };

    updateScheduleSettings(scheduleId, settings);

    toast({
      title: "Settings Saved",
      description: "Invoice payment settings have been updated successfully.",
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Invoice Settings
          </DialogTitle>
          <DialogDescription>
            Configure payment and reminder settings for this schedule's invoices
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Email Reminder */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base">
              <Mail className="h-4 w-4" />
              Send Email Reminder?
            </Label>
            <RadioGroup value={sendEmailReminder ? "yes" : "no"} onValueChange={(v) => setSendEmailReminder(v === "yes")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="email-yes" />
                <Label htmlFor="email-yes" className="font-normal cursor-pointer">Yes, send email reminders</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="email-no" />
                <Label htmlFor="email-no" className="font-normal cursor-pointer">No, don't send reminders</Label>
              </div>
            </RadioGroup>
            
            {sendEmailReminder && (
              <div className="space-y-4 pt-3 border-t">
                <div className="space-y-2">
                  <Label htmlFor="to-emails">To (Press Enter to add)</Label>
                  <Input 
                    id="to-emails"
                    type="email"
                    placeholder="recipient@example.com"
                    value={currentToEmail}
                    onChange={(e) => setCurrentToEmail(e.target.value)}
                    onKeyDown={handleAddToEmail}
                  />
                  {toEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {toEmails.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1">
                          {email}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => removeToEmail(email)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cc-emails">CC (Press Enter to add)</Label>
                  <Input 
                    id="cc-emails"
                    type="email"
                    placeholder="cc@example.com"
                    value={currentCcEmail}
                    onChange={(e) => setCurrentCcEmail(e.target.value)}
                    onKeyDown={handleAddCcEmail}
                  />
                  {ccEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {ccEmails.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1">
                          {email}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => removeCcEmail(email)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
